// ─── Cycle de vie d'un canal Realtime privé face à la session ───────────
//
// Créé le 2026-08-01 à la demande d'Anissa, après l'incident du canal privé
// qui ne se joignait jamais : un canal privé exige le JWT de la session, et
// `setAuth()` sans argument repoussait la clé anonyme.
//
// Cette logique vit ici, hors du composant, pour une raison simple : elle
// décide QUAND un canal privé s'ouvre, se réauthentifie ou se ferme. Ce sont
// des règles de sécurité, elles doivent être testables. Le composant, lui,
// se contente d'exécuter la décision.
//
// RÈGLE DE CONFIDENTIALITÉ : ce module ne renvoie JAMAIS le jeton lui-même
// dans un champ destiné à l'affichage ou au journal. Il indique s'il y en a
// un, et le composant le transmet directement au client Realtime.

/** Décisions possibles face à un événement de session. */
export const DECISION = Object.freeze({
  OUVRIR: 'ouvrir',                 // poser le jeton puis rejoindre le canal
  REMPLACER_JETON: 'remplacer_jeton', // canal déjà ouvert, jeton renouvelé
  FERMER: 'fermer',                 // retirer l'abonnement
  RIEN: 'rien',
});

/**
 * Que faire du canal privé face à un événement d'authentification ?
 *
 * @param {string} evenement - SIGNED_IN | INITIAL_SESSION | TOKEN_REFRESHED |
 *                             SIGNED_OUT | …
 * @param {{ access_token?: string } | null} session
 * @param {boolean} canalOuvert - un canal est-il déjà joint ?
 * @returns {{ decision: string, motif: string }}
 */
export function deciderPourEvenement(evenement, session, canalOuvert) {
  const jeton = session && typeof session.access_token === 'string'
    ? session.access_token.trim()
    : '';

  // La déconnexion prime sur tout : on ferme, quoi qu'il arrive par ailleurs.
  // Sans ça, un canal privé resterait joint avec le jeton d'une session
  // terminée jusqu'au démontage du composant.
  if (evenement === 'SIGNED_OUT') {
    return { decision: DECISION.FERMER, motif: 'session terminee' };
  }

  if (!jeton) {
    // Une session absente ou sans jeton ne permet pas de rejoindre un canal
    // privé. On ne tente pas avec la clé anonyme : le serveur refuserait, et
    // le message d'erreur ferait croire à une panne de transport.
    return { decision: DECISION.RIEN, motif: 'aucun jeton exploitable' };
  }

  if (evenement === 'TOKEN_REFRESHED') {
    // Le jeton a changé. Si le canal est ouvert, il suffit de réauthentifier
    // le client Realtime ; inutile de le rejoindre à nouveau.
    return canalOuvert
      ? { decision: DECISION.REMPLACER_JETON, motif: 'jeton renouvele' }
      : { decision: DECISION.OUVRIR, motif: 'jeton renouvele, canal absent' };
  }

  if (evenement === 'SIGNED_IN' || evenement === 'INITIAL_SESSION') {
    // INITIAL_SESSION est le cas du RECHARGEMENT de page avec session
    // restaurée : c'est précisément celui que supabase-js ne couvre pas.
    return canalOuvert
      ? { decision: DECISION.RIEN, motif: 'canal deja ouvert' }
      : { decision: DECISION.OUVRIR, motif: 'session disponible' };
  }

  return { decision: DECISION.RIEN, motif: `evenement non gere : ${evenement}` };
}

/**
 * Message de journal pour une décision. Volontairement construit ici, pour
 * garantir qu'AUCUN jeton, même tronqué, ne parte dans la console.
 */
export function journalPourDecision(decision, motif) {
  return `[drafts-badge] session : ${decision} (${motif})`;
}

/**
 * Nettoie un message d'erreur AVANT journalisation.
 *
 * Les messages d'erreur du client Realtime ne sont pas de nous : ils peuvent
 * contenir l'URL du websocket, laquelle porte `apikey=<jeton>`. Consigne
 * d'Anissa (2026-08-01) : aucun jeton, même partiel, ne doit apparaître dans
 * la console. On masque donc tout ce qui ressemble à un JWT ou à un
 * paramètre de jeton, plutôt que de faire confiance à la bibliothèque.
 */
export function nettoyerMessageErreur(message) {
  if (typeof message !== 'string' || message === '') return '';
  // ORDRE IMPORTANT. Les paramètres de requête d'abord : ils consomment
  // `apikey=<jwt>` en entier. Si les motifs JWT passaient avant, ils
  // laisseraient un `[jeton masque]` que la seconde règle recouperait, et le
  // message deviendrait illisible (constaté en test).
  return message
    // Paramètres de requête portant un secret.
    .replace(/([?&](?:apikey|access_token|token)=)[^&\s'"]*/gi, '$1[masque]')
    // JWT complet : trois segments base64url séparés par des points.
    .replace(/eyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[jeton masque]')
    // Un « eyJ… » isolé, cas d'un jeton tronqué dans le message.
    .replace(/eyJ[A-Za-z0-9_-]{8,}/g, '[jeton masque]');
}
